import React from 'react';
import LegalLayout from '../components/LegalLayout';
import { useTranslation } from 'react-i18next';

const CookiePolicy: React.FC = () => {
  const { t } = useTranslation();
  return (
    <LegalLayout title={t('legalPages.cookies.title')} lastUpdated={t('legalPages.lastUpdatedDate')}>
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('legalPages.cookies.what.title')}</h2>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.what.p1')}
          </p>
          <p className="text-gray-700">
            {t('legalPages.cookies.what.p2')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('legalPages.cookies.types.title')}</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">{t('legalPages.cookies.types.essential.title')}</h3>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.types.essential.p')}
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
            <li>{t('legalPages.cookies.types.essential.items.0')}</li>
            <li>{t('legalPages.cookies.types.essential.items.1')}</li>
            <li>{t('legalPages.cookies.types.essential.items.2')}</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">{t('legalPages.cookies.types.perf.title')}</h3>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.types.perf.p')}
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
            <li>{t('legalPages.cookies.types.perf.items.0')}</li>
            <li>{t('legalPages.cookies.types.perf.items.1')}</li>
            <li>{t('legalPages.cookies.types.perf.items.2')}</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">{t('legalPages.cookies.types.marketing.title')}</h3>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.types.marketing.p')}
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>{t('legalPages.cookies.types.marketing.items.0')}</li>
            <li>{t('legalPages.cookies.types.marketing.items.1')}</li>
            <li>{t('legalPages.cookies.types.marketing.items.2')}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('legalPages.cookies.thirdParty.title')}</h2>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.thirdParty.p')}
          </p>
          
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">{t('legalPages.cookies.thirdParty.table.service')}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">{t('legalPages.cookies.thirdParty.table.purpose')}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">{t('legalPages.cookies.thirdParty.table.more')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">Google Analytics</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t('legalPages.cookies.thirdParty.rows.analytics')}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <a href="https://policies.google.com/privacy" className="text-orange-600 hover:text-orange-700">
                      {t('legalPages.cookies.thirdParty.links.google')}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">Google Ads</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t('legalPages.cookies.thirdParty.rows.ads')}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <a href="https://policies.google.com/technologies/ads" className="text-orange-600 hover:text-orange-700">
                      {t('legalPages.cookies.thirdParty.links.ads')}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">Facebook Pixel</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t('legalPages.cookies.thirdParty.rows.facebook')}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <a href="https://www.facebook.com/privacy/explanation" className="text-orange-600 hover:text-orange-700">
                      {t('legalPages.cookies.thirdParty.links.facebook')}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('legalPages.cookies.manage.title')}</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">{t('legalPages.cookies.manage.browser.title')}</h3>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.manage.browser.p')}
          </p>
          
          <div className="bg-gray-100 p-4 rounded-lg mb-4">
            <h4 className="font-semibold text-gray-800 mb-2">{t('legalPages.cookies.manage.linksTitle')}</h4>
            <ul className="text-gray-700 space-y-1">
              <li>• <a href="https://support.google.com/chrome/answer/95647" className="text-orange-600 hover:text-orange-700">Google Chrome</a></li>
              <li>• <a href="https://support.mozilla.org/pt/kb/protecao-rastreamento-firefox" className="text-orange-600 hover:text-orange-700">Mozilla Firefox</a></li>
              <li>• <a href="https://support.apple.com/pt-pt/guide/safari/sfri11471/mac" className="text-orange-600 hover:text-orange-700">Safari</a></li>
              <li>• <a href="https://support.microsoft.com/pt-pt/help/17442/windows-internet-explorer-delete-manage-cookies" className="text-orange-600 hover:text-orange-700">Internet Explorer</a></li>
            </ul>
          </div>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">{t('legalPages.cookies.manage.center.title')}</h3>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.manage.center.p')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('legalPages.cookies.impact.title')}</h2>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.impact.p')}
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>{t('legalPages.cookies.impact.items.0')}</li>
            <li>{t('legalPages.cookies.impact.items.1')}</li>
            <li>{t('legalPages.cookies.impact.items.2')}</li>
            <li>{t('legalPages.cookies.impact.items.3')}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('legalPages.cookies.duration.title')}</h2>
          <p className="text-gray-700 mb-4">
            {t('legalPages.cookies.duration.p')}
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>{t('legalPages.cookies.duration.items.0')}</li>
            <li>{t('legalPages.cookies.duration.items.1')}</li>
            <li>{t('legalPages.cookies.duration.items.2')}</li>
            <li>{t('legalPages.cookies.duration.items.3')}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('legalPages.cookies.updates.title')}</h2>
          <p className="text-gray-700">
            {t('legalPages.cookies.updates.p')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('legalPages.contact.title')}</h2>
          <p className="text-gray-700 mb-4">
            {t('legalPages.contact.p')}
          </p>
          <div className="bg-gray-100 p-4 rounded-lg">
            <p className="text-gray-700"><strong>{t('legalPages.contact.email')}</strong> privacy@tapeat.com</p>
            <p className="text-gray-700"><strong>{t('legalPages.contact.phone')}</strong> +351 968 682 315</p>
            <p className="text-gray-700"><strong>{t('legalPages.contact.address')}</strong> Açores, Portugal</p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
};

export default CookiePolicy;